import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

interface ActiveSectionValue {
  active: string | null;
  setActive: (id: string | null) => void;
}

const ActiveSectionContext = createContext<ActiveSectionValue | null>(null);

export const ActiveSectionProvider = ({ children }: { children: ReactNode }) => {
  const [active, setActive] = useState<string | null>(null);
  const value = useMemo(() => ({ active, setActive }), [active]);
  return (
    <ActiveSectionContext.Provider value={value}>
      {children}
    </ActiveSectionContext.Provider>
  );
};

const useActiveSectionContext = () => {
  const ctx = useContext(ActiveSectionContext);
  if (!ctx) throw new Error("ActiveSectionProvider ausente");
  return ctx;
};

/** Seção da Home visível no momento (lida pela Navbar). */
export const useActiveSection = () => useActiveSectionContext().active;

/**
 * Observa as seções com os `ids` informados e publica qual está no meio da tela.
 * Chamado pela própria página, então os elementos já existem no DOM.
 */
export const useTrackActiveSection = (ids: string[]) => {
  const { setActive } = useActiveSectionContext();
  const key = ids.join("|");

  useEffect(() => {
    const elements = key
      .split("|")
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => el !== null);

    const visible = new Set<string>();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) visible.add(entry.target.id);
          else visible.delete(entry.target.id);
        });
        const current = elements.filter((el) => visible.has(el.id)).pop();
        setActive(current?.id ?? null);
      },
      { rootMargin: "-45% 0px -50% 0px" },
    );

    elements.forEach((el) => observer.observe(el));
    return () => {
      observer.disconnect();
      setActive(null);
    };
  }, [key, setActive]);
};
